import CurrencyLogo from 'components/CurrencyLogo'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import Loader from 'components/Loader'
import { DynamicSizedLoadingRow } from 'components/Loader/styled'
import { MouseoverTooltip } from 'components/Tooltip'
import { useTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useKodiakFarmLockedStakesOf } from 'hooks/useKodiakFarm'
import useTheme from 'hooks/useTheme'
import { ConfigureButton } from 'pages/Liquidity/ProtocolLists/farms/components/ConfigureButton'
import { FarmTypeDisplay } from 'pages/Liquidity/ProtocolLists/farms/components/FarmTypeDisplay'
import { memo, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { ThemedText } from 'theme'
import { KodiakFarm, StakingTokenType } from 'types/farms'
import { formatDollarAmount } from 'utils/formatDollarAmt'

import * as S from '../../IslandList/IslandListItem/styled'
import { PrimaryPositionIdData } from '../../IslandList/IslandListItem/styled'
import { Td } from '../styled'

const PairText = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: ${({ theme }) => theme.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  text-wrap: nowrap;
  min-height: 20px;
  display: flex;
  align-items: center;
`

const SymbolSkeleton = styled.div`
  height: 14px;
  width: 60px;
  background: ${({ theme }) => theme.backgroundInteractive};
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite alternate;

  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    100% {
      opacity: 0.9;
    }
  }
`

const StatusIndicator = styled.div<{ isActive: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ isActive, theme }) => (isActive ? theme.accentSuccess : theme.textSecondary)};
  margin-right: 8px;
  flex-shrink: 0;
`

const TokenInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`

const TokenSymbolRow = styled.div`
  display: flex;
  align-items: center;
`

const LogoContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 56px;
  margin-right: 12px;
`

interface FarmListItemProps {
  farm: KodiakFarm
  row: number
}

const LoadingSkeleton = () => (
  <DynamicSizedLoadingRow
    style={{ width: '100%' }}
    height="32px"
    borderRadius="0"
    borderTopLeftRadius="0"
    borderTopRightRadius="0"
    borderBottomLeftRadius="0"
    borderBottomRightRadius="0"
  >
    <div />
  </DynamicSizedLoadingRow>
)

const getFarmStatus = (farm: KodiakFarm) => {
  const isActive = farm.isActive && !farm.isPaused && !farm.stakingPaused
  const statusText = isActive ? 'Active' : 'Inactive'

  let tooltipText = `Farm Status: ${statusText}`
  if (farm.isPaused) tooltipText += ' (Paused)'
  if (farm.stakingPaused) tooltipText += ' (Staking Paused)'
  if (!farm.isActive) tooltipText += ' (Not Active)'

  return { isActive, statusText, tooltipText }
}

const areEqual = (prevProps: FarmListItemProps, nextProps: FarmListItemProps) => {
  const farmPropsEqual =
    prevProps.farm.id === nextProps.farm.id &&
    prevProps.farm.stakingToken.id === nextProps.farm.stakingToken.id &&
    prevProps.farm.stakingTokenType === nextProps.farm.stakingTokenType &&
    prevProps.farm.totalLiquidityLocked === nextProps.farm.totalLiquidityLocked &&
    prevProps.farm.lockMaxMultiplier === nextProps.farm.lockMaxMultiplier &&
    prevProps.farm.isActive === nextProps.farm.isActive &&
    prevProps.farm.isPaused === nextProps.farm.isPaused &&
    prevProps.farm.stakingPaused === nextProps.farm.stakingPaused

  return farmPropsEqual && prevProps.row === nextProps.row
}

const FarmListItem = memo(function FarmListItem({ farm, row }: FarmListItemProps) {
  const { account } = useConnection()

  const { data: stakedResponse, isLoading: isLoadingUserStakedUSDValue } = useKodiakFarmLockedStakesOf(farm, account)

  const isGenericFarm = farm.stakingTokenType === StakingTokenType.GENERIC
  const tokenAddresses = useMemo(() => {
    return isGenericFarm ? [farm.stakingToken.id] : [farm.vault?.token0.id, farm.vault?.token1.id].filter(Boolean)
  }, [isGenericFarm, farm.stakingToken.id, farm.vault?.token0.id, farm.vault?.token1.id])

  const tokens = useTokens(tokenAddresses)

  const { token0, token1, staking_token } = useMemo(() => {
    if (isGenericFarm) {
      return { token0: undefined, token1: undefined, staking_token: tokens[0] }
    }
    return { token0: tokens[0], token1: tokens[1], staking_token: undefined }
  }, [isGenericFarm, tokens])
  const [isTokenLoading, setIsTokenLoading] = useState(true)

  const theme = useTheme()
  const farmStatus = getFarmStatus(farm)

  const hasStakedAmount = (stakedResponse?.totalLiquidityUSD || 0) > 0

  useEffect(() => {
    if (staking_token !== undefined || token0 || token1) {
      setIsTokenLoading(false)
    }
  }, [staking_token, token0, token1])

  const routeTo =
    farm.stakingTokenType === StakingTokenType.GENERIC
      ? `/liquidity/farms/single/${farm.id}`
      : `/liquidity/pools/${farm.stakingToken.id}?farm=${farm.id}`

  if (isTokenLoading) {
    return (
      <tbody>
        <tr>
          <td colSpan={6} style={{ width: '100%' }}>
            <LoadingSkeleton />
          </td>
        </tr>
      </tbody>
    )
  }

  const depositContent = (
    <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="24px" color={theme.textSecondary}>
      {isLoadingUserStakedUSDValue ? <Loader /> : formatDollarAmount(stakedResponse?.totalLiquidityUSD || 0)}
    </ThemedText.DeprecatedBody>
  )

  return (
    <S.StyledLink to={routeTo} state={{ returnTo: '/liquidity/farms' }} style={{ cursor: 'pointer' }}>
      <Td>
        <PrimaryPositionIdData>
          <LogoContainer>
            {farm.stakingTokenType === StakingTokenType.GENERIC ? (
              <CurrencyLogo currency={staking_token || undefined} size="32px" needFallbackOnLoading />
            ) : (
              <DoubleCurrencyLogo currency0={token0 || undefined} currency1={token1 || undefined} size={32} margin />
            )}
          </LogoContainer>
          <TokenInfoContainer>
            <TokenSymbolRow>
              <MouseoverTooltip text={farmStatus.tooltipText}>
                <StatusIndicator isActive={farmStatus.isActive} />
              </MouseoverTooltip>
              <PairText>
                {farm.stakingTokenType === StakingTokenType.GENERIC
                  ? staking_token?.symbol || farm.stakingToken.symbol
                  : `${token0?.symbol} - ${token1?.symbol}`}
              </PairText>
            </TokenSymbolRow>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FarmTypeDisplay type={farm.stakingTokenType} />
            </div>
          </TokenInfoContainer>
        </PrimaryPositionIdData>
      </Td>

      <Td>
        <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="24px" color={theme.textPrimary}>
          {farm.apr ? `${farm.apr.toFixed(2)}%` : '0.00%'}
        </ThemedText.DeprecatedBody>
      </Td>

      <Td>
        <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="24px" color={theme.textPrimary}>
          {formatDollarAmount(farm.tvl || 0)}
        </ThemedText.DeprecatedBody>
      </Td>

      <Td>
        <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="24px" color={theme.textPrimary}>
          {farm.vault ? formatDollarAmount(farm.vault.tvl || 0) : '-'}
        </ThemedText.DeprecatedBody>
      </Td>

      <Td>
        {hasStakedAmount ? (
          <MouseoverTooltip
            text={
              <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                <ThemedText.DeprecatedBody>
                  {`Total Staked: ${formatDollarAmount(stakedResponse?.totalLiquidityUSD || 0)}`}
                  <br />
                  {`Lock Range: ${parseInt(farm.lockTimeMin) / 86400}-${parseInt(farm.lockTimeMax) / 86400} days`}
                </ThemedText.DeprecatedBody>
              </ThemedText.DeprecatedBody>
            }
          >
            {depositContent}
          </MouseoverTooltip>
        ) : (
          depositContent
        )}
      </Td>

      <Td>
        <ConfigureButton farmAddress={farm.id} />
      </Td>
    </S.StyledLink>
  )
}, areEqual)

export default FarmListItem
